import type { ChainFrameFit } from '@/lib/text/types/chain-frame-fit'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'
import { isShapeBackedFrame } from '@/lib/text-linking/linkable-text-frame'
import { fitParagraphsToShape } from '@/lib/shapes/fit-paragraphs-to-shape'
import type { ShapeTextWrapContext } from '@/lib/shapes/types/shape-text-wrap-context'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import { logger } from '@/utils/logger'

/**
 * A chain member's own fitter: its real outline, measured by the shape-text engine. **The single
 * builder for every `StoryFrame.fit` in home** — `buildChainStoryFrames` and `handleChainOverflow`
 * both go through it, so no call site can measure a frame differently from the others.
 *
 * `wrap` is the text-wrap context the RENDERER lays this frame out with, read straight off
 * `ShapeManagerRef.getShapeTextWrapContext()`. Passing anything else (including nothing) reopens the
 * divergence: a chain member sitting under a wrap source would measure its capacity with no
 * exclusion zones and hand off at an offset it never paints to. `undefined` is correct and normal —
 * the manager returns it whenever the spread has no wrap source at all.
 *
 * The returned closure forwards its `ParagraphBoxFitOptions` to the engine, so a caller can ask for
 * a character-accurate seam (`fillToCharacter`). Deliberately per-CALL rather than baked in here:
 * whether a frame has somewhere to send its remainder is decided by the walk that uses the fitter
 * (`distributeStory` derives it from the last-frame early-out) and by `storyFromChain`'s seam probe,
 * not by whichever chain snapshot happened to be in scope when the closure was constructed.
 *
 * Every chain member is shape-backed (`collectLinkableFrames` projects shapes only). A frame without
 * a shape has no outline to measure, so it reports no overflow rather than inventing a capacity.
 */
export function chainFrameFit(
  frame: LinkableTextFrame,
  fonts?: ParagraphBlockFontLookup,
  wrap?: ShapeTextWrapContext
): ChainFrameFit {
  if (!isShapeBackedFrame(frame)) {
    logger.warn('chainFrameFit: chain member has no shape to measure', { frameId: frame.id })

    return () => null
  }

  return (paragraphs, options) =>
    fitParagraphsToShape(paragraphs, frame.shape, fonts, options, wrap)
}
